import { useEffect } from 'react';

/**
 * Site-wide identity and per-page <head> management for the non-game pages.
 * Everything SEO hangs off this file: titles, descriptions, canonicals, Open
 * Graph and Twitter tags, and JSON-LD structured data.
 *
 * SITE.origin is the single switch for the future domain. While it is empty,
 * absolute-URL features (canonical links, og:url, sitemap entries, breadcrumb
 * JSON-LD URLs) are simply omitted; set it once the domain exists and rebuild.
 * See SITE_NOTES.md for the full go-live checklist.
 */
export const SITE = {
  /** No trailing slash, e.g. 'https://bigmechenergy.com'. Empty until the domain is bought. */
  origin: 'https://bigmechenergy.com',
  name: 'Big Mech Energy Idle',
  shortName: 'Big Mech Energy',
  tagline: 'A free browser idle game about one loyal AI and a series of increasingly large mechs.',
  description:
    'Big Mech Energy Idle is a free browser idle game. Wire salvaged parts into your mech, fight the machine uprising, beat 11 AI Overlords and upload into 12 ever-bigger war-frames. No account, no download.',
  discordUrl: 'https://discord.com/invite/KFGhGmQWKs',
  devHandle: 'lifeingray',
  themeColor: '#090d13',
  ogImagePath: '/media/og.png',
  /** Content dates for Article structured data. Bump `updated` with notable
   *  content changes; `published` is the wiki's first public date. */
  published: '2026-06-01',
  updated: '2026-06-13',
};

export interface PageMeta {
  /** Normalized route path, e.g. '/wiki/foundry'. */
  path: string;
  title: string;
  description: string;
  /** Extra JSON-LD blocks beyond the page defaults. */
  jsonLd?: object[];
  /** Wiki pages get breadcrumb structured data. */
  breadcrumb?: { name: string; path: string }[];
  /** Error pages opt out of indexing. */
  noindex?: boolean;
  /** Emit TechArticle structured data (the mechanics guides). */
  article?: boolean;
  /** Advertise a Markdown mirror via <link rel="alternate" type="text/markdown">. */
  markdownPath?: string;
}

/** Absolute URL for a route when the domain is known, else null. */
export function absUrl(path: string): string | null {
  if (!SITE.origin) return null;
  const p = path === '/' ? '/' : `${path.replace(/\/+$/, '')}/`;
  return SITE.origin + p;
}

/** Per-route social card. The prerender step builds one PNG per route at
 *  /media/og/<slug>.png; this returns its URL (absolute when the domain is set
 *  so scrapers and AI cards resolve it). '/' maps to 'home'. */
export function ogImageUrl(path: string): string {
  const slug = path === '/' ? 'home' : path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-');
  const rel = `/media/og/${slug}.png`;
  return SITE.origin ? SITE.origin + rel : rel;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertAltMarkdown(href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="alternate"][type="text/markdown"]');
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = 'alternate';
    el.type = 'text/markdown';
    document.head.appendChild(el);
  }
  el.href = href;
}

/** TechArticle structured data for the mechanics guides: attribution + dates an
 *  AI engine can cite. */
function articleLd(meta: PageMeta): object {
  const ld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: meta.title,
    description: meta.description,
    inLanguage: 'en',
    image: ogImageUrl(meta.path),
    author: { '@type': 'Person', name: SITE.devHandle },
    publisher: { '@type': 'Person', name: SITE.devHandle },
    datePublished: SITE.published,
    dateModified: SITE.updated,
  };
  const url = absUrl(meta.path);
  if (url) {
    ld.url = url;
    ld.mainEntityOfPage = url;
  }
  return ld;
}

function upsertJsonLd(blocks: object[]): void {
  document.head.querySelectorAll('script[data-bme-jsonld]').forEach((s) => s.remove());
  for (const block of blocks) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-bme-jsonld', '1');
    s.textContent = JSON.stringify(block);
    document.head.appendChild(s);
  }
}

/** The game itself, as schema.org sees it. Honest data only; never a rating. */
export function videoGameLd(): object {
  const ld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: SITE.name,
    description: SITE.description,
    genre: ['Idle', 'Incremental', 'Strategy'],
    gamePlatform: 'Web browser',
    playMode: 'SinglePlayer',
    applicationCategory: 'Game',
    operatingSystem: 'Any',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    author: { '@type': 'Person', name: SITE.devHandle },
  };
  const url = absUrl('/');
  if (url) {
    ld.url = url;
    ld.image = SITE.origin + SITE.ogImagePath;
  }
  return ld;
}

function breadcrumbLd(items: { name: string; path: string }[]): object | null {
  if (!SITE.origin) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: absUrl(it.path),
    })),
  };
}

export function setHead(meta: PageMeta): void {
  document.title = meta.title;
  upsertMeta('name', 'robots', meta.noindex ? 'noindex' : null);
  upsertMeta('name', 'description', meta.description);
  upsertMeta('property', 'og:site_name', SITE.name);
  upsertMeta('property', 'og:type', meta.article ? 'article' : 'website');
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:url', absUrl(meta.path));
  const ogImg = ogImageUrl(meta.path);
  upsertMeta('property', 'og:image', ogImg);
  upsertMeta('property', 'og:image:width', '1200');
  upsertMeta('property', 'og:image:height', '630');
  upsertMeta('property', 'og:image:alt', meta.title);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  upsertMeta('name', 'twitter:image', ogImg);
  upsertCanonical(absUrl(meta.path));
  upsertAltMarkdown(meta.markdownPath ? (SITE.origin ? SITE.origin + meta.markdownPath : meta.markdownPath) : null);

  const blocks: object[] = [videoGameLd()];
  if (meta.article) blocks.push(articleLd(meta));
  if (meta.breadcrumb) {
    const bc = breadcrumbLd(meta.breadcrumb);
    if (bc) blocks.push(bc);
  }
  if (meta.jsonLd) blocks.push(...meta.jsonLd);
  upsertJsonLd(blocks);
}

/** Pages call this once per render of a distinct route. */
export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    setHead(meta);
    // The dependency is the path: a page's meta is static per route.
  }, [meta.path]);
}
